/**
 * Safe date parsing and formatting utilities.
 *
 * IMPORTANT: Never use `new Date("YYYY-MM-DD")` directly in this codebase.
 * JavaScript parses bare date strings as UTC midnight, which in US timezones
 * (UTC-5 to UTC-8) renders as the PREVIOUS day. Always use parseLocalDate()
 * or the formatting functions below.
 */

/** Parse a date string safely — appends T12:00:00 to bare YYYY-MM-DD to avoid UTC midnight timezone shift */
export function parseLocalDate(dateStr: string): Date {
  if (dateStr.includes("T")) return new Date(dateStr)
  return new Date(dateStr + "T12:00:00")
}

/** Format a date string for display. Safe from UTC timezone shift. */
export function formatDate(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateStr) return ""
  return parseLocalDate(dateStr).toLocaleDateString(
    "en-US",
    options ?? { month: "short", day: "numeric", year: "numeric" },
  )
}

/** Format a date as M/D/YYYY */
export function formatUSDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  const d = parseLocalDate(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

/** Format a date as MM-DD-YYYY */
export function formatDashDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  const d = parseLocalDate(dateStr)
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${mm}-${dd}-${d.getFullYear()}`
}

/** Format a date as M/D/YY */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  const d = parseLocalDate(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`
}

/** Format a date range as "Mon D - Mon D" */
export function formatDateRange(start: string, end: string): string {
  const fmt = (d: string) =>
    parseLocalDate(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  return `${fmt(start)} - ${fmt(end)}`
}

/** Return today's date as YYYY-MM-DD in the browser's local timezone */
export function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/** Return current local datetime as YYYY-MM-DDTHH:mm for datetime-local inputs */
export function nowLocalDatetime(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Format a date+time string for display. Safe from UTC timezone shift. */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  return parseLocalDate(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** Add days to a date string and return YYYY-MM-DD */
export function addDays(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr)
  d.setDate(d.getDate() + days)
  return d.toISOString().split("T")[0]
}
